import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Icon, Table, Dropdown, Checkbox, Message, Label, Loader } from 'semantic-ui-react';
import { useAvailableICcIdsForFastchipSwap } from '../../hooks/useICcIds';
import { ICcIdResponse } from '../../types/dtos';

const MOTIVOS_TROCA = [
  { key: 'Reposicao', text: 'Reposição', value: 'Reposicao' },
  { key: 'FastIndisponivel', text: 'Fast Indisponível', value: 'FastIndisponivel' },
  { key: 'Errado', text: 'Errado', value: 'Errado' },
  { key: 'Defeito', text: 'Defeito', value: 'Defeito' },
  { key: 'Outro', text: 'Outro', value: 'Outro' },
];

interface SelectedLine {
  id: string;
  contractId?: string;
  ddd?: string;
  planName?: string;
  chipCcid?: string;
  negociacao?: string;
  /** Se a linha usa e-SIM (chip virtual via QR Code) */
  isEsim?: boolean;
}

interface LineSwapState {
  newIccid: string | null;
  motivoTroca: string;
  fastUsado: boolean;
}

interface FastchipSwapModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (swaps: Array<{ lineId: string; contractId?: string; newIccid: string | null; motivoTroca: string; fastUsado: boolean }>) => Promise<void>;
  lines: SelectedLine[];
  clientEstado: string;
  /** CCID do primeiro chip em linha Aditivo (ordem do contrato). Quando definido, o stock vem da API filtrada por cluster/UF desse ICCID. */
  referenceIccid?: string | null;
  currentIccids?: string[];
}

export const FastchipSwapModal: React.FC<FastchipSwapModalProps> = ({
  open,
  onClose,
  onConfirm,
  lines,
  clientEstado,
  referenceIccid,
  currentIccids,
}) => {
  const referenceKey = referenceIccid?.trim() || '';
  const usesReferencePool = referenceKey.length > 0;
  const { iccids: availableICcIdsRaw, loading: iccidsLoading } = useAvailableICcIdsForFastchipSwap(
    referenceIccid,
    clientEstado
  );
  const [lineStates, setLineStates] = useState<Record<string, LineSwapState>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pool base: disponíveis, excluindo os já em uso no contrato (mas não os ICCIDs atuais das linhas selecionadas)
  const baseAvailableICcIds = useMemo(() => {
    const currentSet = new Set(currentIccids || []);
    let filtered = availableICcIdsRaw
      .filter((ic: ICcIdResponse) => ic.disponivel === true)
      .filter((ic: ICcIdResponse) => !currentSet.has(ic.ccId));

    if (!usesReferencePool && (clientEstado === 'MG' || clientEstado === 'RJ')) {
      filtered = filtered.filter((ic: ICcIdResponse) => {
        const estados = ic.estados || (ic.estado ? [ic.estado] : []);
        return estados.some((e: string) => e === 'MG' || e === 'RJ');
      });
    }

    return filtered;
  }, [availableICcIdsRaw, clientEstado, currentIccids, usesReferencePool]);

  /** Infere o cluster do ICCID atual de uma linha a partir da lista completa (incluindo não disponíveis) */
  const getClusterForIccid = (ccid?: string): string | null => {
    if (!ccid || clientEstado !== 'PR') return null;
    const iccid = availableICcIdsRaw.find((ic: ICcIdResponse) => ic.ccId === ccid);
    const prInfo = iccid?.estadosInfo?.find((e: { estado: string; cluster?: string | null }) => e.estado === 'PR');
    return prInfo?.cluster ?? null;
  };

  /** Retorna as opções de ICCID disponíveis para uma linha específica */
  const getOptionsForLine = (line: SelectedLine, selectedIccids: (string | null)[]) => {
    let pool = baseAvailableICcIds;

    // Filtro cluster PR: inferir o cluster do ICCID atual e restringir ao mesmo
    if (clientEstado === 'PR' && !usesReferencePool) {
      const currentCluster = getClusterForIccid(line.chipCcid);
      if (currentCluster) {
        pool = pool.filter((ic: ICcIdResponse) => {
          const prInfo = ic.estadosInfo?.find((e: { estado: string; cluster?: string | null }) => e.estado === 'PR');
          return prInfo?.cluster === currentCluster;
        });
      }
    }

    // Filtro e-SIM: e-SIM só troca com e-SIM, chip físico só troca com chip físico
    if (line.isEsim === true) {
      pool = pool.filter((ic: ICcIdResponse) => ic.isEsim === true);
    } else {
      pool = pool.filter((ic: ICcIdResponse) => !ic.isEsim);
    }

    return pool.map((ic: ICcIdResponse) => ({
      key: ic.id,
      text: `${ic.ccId.slice(-8)} (${ic.ccId})${ic.isEsim ? ' [e-SIM]' : ''}`,
      value: ic.ccId,
      disabled: selectedIccids.includes(ic.ccId),
      description: ic.estados?.join(', ') || ic.estado || '',
    }));
  };

  useEffect(() => {
    if (open) {
      const initial: Record<string, LineSwapState> = {};
      lines.forEach(line => {
        initial[line.id] = { newIccid: null, motivoTroca: '', fastUsado: false };
      });
      setLineStates(initial);
      setError(null);
    }
  }, [open, lines]);

  const updateLineState = (lineId: string, updates: Partial<LineSwapState>) => {
    setLineStates(prev => ({
      ...prev,
      [lineId]: { ...prev[lineId], ...updates },
    }));
  };

  const isValid = useMemo(() => {
    return lines.every(line => {
      const state = lineStates[line.id];
      if (!state) return false;
      return state.newIccid && state.motivoTroca;
    });
  }, [lines, lineStates]);

  const handleConfirm = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const swaps = lines.map(line => ({
        lineId: line.id,
        contractId: line.contractId,
        newIccid: lineStates[line.id].newIccid,
        motivoTroca: lineStates[line.id].motivoTroca,
        fastUsado: lineStates[line.id].fastUsado,
      }));
      await onConfirm(swaps);
    } catch (err: any) {
      setError(err.message || 'Erro ao processar troca de FastChips');
    } finally {
      setSubmitting(false);
    }
  };

  const hasClusterFilter = !usesReferencePool && clientEstado === 'PR' && lines.some(l => getClusterForIccid(l.chipCcid));
  const hasEsimLines = lines.some(l => l.isEsim);

  return (
    <Modal open={open} onClose={onClose} size="large" closeOnDimmerClick={false}>
      <Modal.Header>
        <Icon name="exchange" color="blue" />
        Trocar FastChips — {lines.length} linha{lines.length > 1 ? 's' : ''} selecionada{lines.length > 1 ? 's' : ''}
      </Modal.Header>
      <Modal.Content scrolling>
        {error && (
          <Message negative>
            <Message.Header>Erro</Message.Header>
            <p style={{ whiteSpace: 'pre-line' }}>{error}</p>
          </Message>
        )}

        {(hasClusterFilter || hasEsimLines || usesReferencePool) && (
          <Message info size="small">
            {usesReferencePool && (
              <p>
                <Icon name="filter" />
                Lista de novos ICCIDs alinhada ao cadastro regional do primeiro chip Aditivo do contrato. Referência:{' '}
                <code style={{ fontSize: '0.9em' }}>{referenceKey}</code>
              </p>
            )}
            {hasClusterFilter && (
              <p>
                <Icon name="filter" />
                Filtrando ICCIDs pelo cluster do FAST atual de cada linha (Paraná).
              </p>
            )}
            {hasEsimLines && (
              <p>
                <Icon name="qrcode" />
                Linhas e-SIM só podem trocar com outros ICCIDs e-SIM.
              </p>
            )}
          </Message>
        )}

        {iccidsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Loader active inline="centered" content="Carregando ICCIDs disponíveis..." />
          </div>
        ) : baseAvailableICcIds.length === 0 ? (
          <Message warning>
            <Message.Header>Sem ICCIDs disponíveis</Message.Header>
            <p>
              {usesReferencePool
                ? 'Não há ICCIDs disponíveis compatíveis com o chip de referência (mesma UF/cluster no cadastro) ou já estão atribuídos a outras linhas deste contrato.'
                : clientEstado
                  ? `Não há ICCIDs disponíveis para o estado ${clientEstado}.`
                  : 'Não há ICCIDs disponíveis: falta a UF do cliente ou uma linha Aditivo com chip para definir o stock.'}
            </p>
          </Message>
        ) : (
          <Table celled structured>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={2}>DDD</Table.HeaderCell>
                <Table.HeaderCell width={3}>Plano</Table.HeaderCell>
                <Table.HeaderCell width={3}>ICCID Atual</Table.HeaderCell>
                <Table.HeaderCell width={4}>Novo ICCID</Table.HeaderCell>
                <Table.HeaderCell width={3}>Motivo</Table.HeaderCell>
                <Table.HeaderCell width={1} textAlign="center">Fast Usado</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {lines.map(line => {
                const state = lineStates[line.id] || { newIccid: null, motivoTroca: '', fastUsado: false };
                const selectedIccids = Object.values(lineStates).map(s => s.newIccid);
                const options = getOptionsForLine(line, selectedIccids);
                const currentCluster = getClusterForIccid(line.chipCcid);
                return (
                  <Table.Row key={line.id}>
                    <Table.Cell>
                      <Label color="blue" size="small">{line.ddd || '-'}</Label>
                      {line.isEsim && <Label color="purple" size="mini" style={{ marginLeft: '4px' }}>e-SIM</Label>}
                    </Table.Cell>
                    <Table.Cell>{line.planName || '-'}</Table.Cell>
                    <Table.Cell>
                      <code style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>
                        {line.chipCcid || '-'}
                      </code>
                      {currentCluster && (
                        <Label color="teal" size="mini" style={{ marginLeft: '4px' }}>{currentCluster}</Label>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {options.length === 0 ? (
                        <span style={{ color: '#856404', fontSize: '0.9em' }}>
                          Nenhum ICCID{line.isEsim ? ' e-SIM' : ''} disponível
                          {currentCluster ? ` (cluster ${currentCluster})` : ''}
                        </span>
                      ) : (
                        <Dropdown
                          placeholder="Selecione o ICCID"
                          fluid
                          selection
                          search
                          options={options}
                          value={state.newIccid || ''}
                          onChange={(_, data) => updateLineState(line.id, { newIccid: data.value as string })}
                          loading={iccidsLoading}
                          noResultsMessage="Nenhum ICCID encontrado"
                        />
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Dropdown
                        placeholder="Motivo"
                        fluid
                        selection
                        options={MOTIVOS_TROCA}
                        value={state.motivoTroca}
                        onChange={(_, data) => {
                          const motivo = data.value as string;
                          updateLineState(line.id, {
                            motivoTroca: motivo,
                            ...(motivo === 'FastIndisponivel' ? { fastUsado: true } : {}),
                          });
                        }}
                      />
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Checkbox
                        toggle
                        checked={state.fastUsado}
                        onChange={(_, data) => updateLineState(line.id, { fastUsado: data.checked ?? false })}
                      />
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}

        <Message info size="small">
          <Message.List>
            <Message.Item>Selecione um novo ICCID da lista para cada linha.</Message.Item>
            <Message.Item>Informe o motivo da troca para cada linha.</Message.Item>
            <Message.Item>
              Marque <strong>"Fast Usado"</strong> se o chip antigo deve ficar indisponível (estoque zerado).
            </Message.Item>
          </Message.List>
        </Message>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          primary
          onClick={handleConfirm}
          disabled={!isValid || submitting || baseAvailableICcIds.length === 0}
          loading={submitting}
        >
          <Icon name="check" />
          Confirmar Troca{lines.length > 1 ? 's' : ''}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
